import { useEffect, useRef } from 'react';
import * as React from 'react';
import { observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';

import { store } from 'Models/Store';
import { cancelManualRefund } from 'Services/Api/_HumanWritten/BookingService/BookingService';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import alertToast from 'Util/ToastifyUtils';
import { stringNotEmpty } from 'Util/TypeGuards';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import { TextArea } from 'Views/Components/TextArea/TextArea';
import { RefundData, RefundNoteModel } from './ManualRefundModal';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';

type CancelManualRefundModalProps = {
	refund: RefundData;
	refresh?: () => void;
	onClose: () => void;
};

// A cancellation reason is always required when cancelling a manual refund (regardless of the
// tenant's manual-refund-note config), so there is always a record of why it was cancelled.
const validateNote = (noteModel: RefundNoteModel): boolean => {
	let valid = true;

	runInAction(() => {
		if (!noteModel.note || noteModel.note.trim().length === 0) {
			noteModel.noteError = 'Note is required';
			valid = false;
			return;
		}

		if (noteModel.note.trim().length > 500) {
			noteModel.noteError = 'Note must be less than 500 characters';
			valid = false;
			return;
		}

		noteModel.noteError = '';
	});

	return valid;
};

const CancelManualRefundModalContent = observer(
	({ refund, noteModel }: { refund: RefundData; noteModel: RefundNoteModel }) => {
		return (
			<div className="manual-refund-modal-content">
				<MessageBox status="warning" slim>
					{'This cancels a pending manual refund so it will no longer be paid out. This can not be undone.'}
				</MessageBox>
				<p>
					Please confirm you want to cancel the pending manual refund of
					<strong> {formatPriceDisplay(refund.amount, false, true)}</strong>.
				</p>

				<div className="form-group">
					<TextArea
						id="cancel-refund-note"
						label="Cancellation Note"
						model={noteModel}
						modelProperty="note"
						isRequired
						placeholder="Please provide the reason this manual refund is being cancelled"
						onAfterChange={e => {
							runInAction(() => {
								noteModel.note = e.target.value;
								if (noteModel.noteError) {
									noteModel.noteError = '';
								}
							});
						}}
					/>
					<div className="character-count">{noteModel.note.length}/500</div>
					{noteModel.noteError && <div className="invalid-feedback">{noteModel.noteError}</div>}
				</div>
			</div>
		);
	},
);

const buildCancelManualRefundModalContent = (refund: RefundData, noteModel: RefundNoteModel): JSX.Element => {
	return <CancelManualRefundModalContent refund={refund} noteModel={noteModel} />;
};

export const CancelManualRefundModal: React.FC<CancelManualRefundModalProps> = observer(
	({ refund, refresh, onClose }) => {
		const modalShownRef = useRef(false);

		const showLoadingModal = (text: string, disableButtons = true) => {
			return newConfirmModal(
				'Cancelling Manual Refund',
				<div className="loader-container">
					<div className="spinner-dots">
						<div className="dot" />
						<div className="dot" />
						<div className="dot" />
					</div>
					<div className="loading-text dots">{text}</div>
				</div>,
				{
					cancelText: 'Back',
					confirmText: 'Confirm',
					actionClassName: 'manual-refund-modal',
					resolveOnCancel: false,
					disableConfirmButton: disableButtons,
					disableCancelButton: disableButtons,
				},
			);
		};

		useEffect(() => {
			if (!modalShownRef.current) {
				modalShownRef.current = true;

				const noteModel = observable({
					note: '',
					noteError: '',
				});

				newConfirmModal('Cancel manual refund', buildCancelManualRefundModalContent(refund, noteModel), {
					cancelText: 'Back',
					confirmText: 'Cancel Refund',
					actionClassName: 'manual-refund-modal',
					resolveOnCancel: false,
					onBeforeConfirm: async () => {
						const isValid = validateNote(noteModel);
						if (!isValid) {
							return false;
						}

						try {
							showLoadingModal('Cancelling manual refund...');

							await cancelManualRefund(refund.id, noteModel.note.trim());

							store.modal.hide();

							alertToast('Successfully cancelled manual refund', 'success');

							if (refresh) {
								refresh();
							}

							onClose?.();

							return true;
						} catch (error: any) {
							store.modal.hide();

							console.error('Error cancelling manual refund:', error);
							if (stringNotEmpty(error?.response?.data)) {
								alertToast(`Error cancelling manual refund: ${error?.response?.data}`, 'error');
								if (refresh) {
									refresh();
								}
							} else {
								alertToast('An error occurred while cancelling the manual refund', 'error');
							}

							onClose?.();

							return false;
						}
					},
				}).catch(() => {
					onClose?.();
				});
			}
		}, [refund, refresh, onClose]);

		return null;
	},
);
